import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const services = [
    { name: 'Krankengymnastik', description: 'Individuelle Bewegungstherapie zur Wiederherstellung der Beweglichkeit' },
    { name: 'Manuelle Therapie', description: 'Gezielte Mobilisation von Gelenken und Weichteilen' },
    { name: 'Lymphdrainage', description: 'Sanfte Entstauungstherapie bei Schwellungen' },
    { name: 'Massage', description: 'Klassische und therapeutische Massagetechniken' },
    { name: 'Elektrotherapie', description: 'Schmerzlinderung durch elektrische Impulse' },
    { name: 'Stoßwellentherapie', description: 'Moderne Behandlung bei chronischen Beschwerden' }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">Physiotherapie Grau</h1>
            <p className="hero-subtitle">Ihre Physiotherapiepraxis in Apolda</p>
            <p className="hero-description">
              Familienbetrieb mit persönlicher Betreuung und individueller Behandlung. 
              Wir nehmen uns Zeit für Ihre Gesundheit.
            </p>
            <div className="hero-buttons">
              <Button asChild size="lg" className="cta-button">
                <Link to="/kontakt">Termin vereinbaren</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="secondary-button">
                <Link to="/leistungen">Unsere Leistungen</Link>
              </Button>
            </div>
          </div>
          <div className="hero-image">
            <img 
              src="https://customer-assets.emergentagent.com/job_physiograu/artifacts/50mmofh2_IMG_7217.JPG" 
              alt="Empfangsbereich Physiotherapie Grau"
            />
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="services-overview">
        <div className="section-container">
          <h2 className="section-title">Unsere Leistungen</h2>
          <p className="section-description">
            Umfassende physiotherapeutische Behandlung auf Verordnung Ihres Arztes
          </p>
          <div className="services-grid">
            {services.map((service, index) => (
              <Card key={index} className="service-card">
                <CardContent className="service-card-content">
                  <h3 className="service-name">{service.name}</h3>
                  <p className="service-description">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="services-cta">
            <Button asChild size="lg" variant="outline">
              <Link to="/leistungen">
                Alle Leistungen ansehen
                <ChevronRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Hausbesuche Section */}
      <section className="hausbesuche-section">
        <div className="section-container hausbesuche-content">
          <div className="hausbesuche-text">
            <h2 className="section-title">Hausbesuche in Apolda</h2>
            <p className="hausbesuche-description">
              Wir kommen zu Ihnen nach Hause, wenn die Mobilität eingeschränkt ist oder 
              Sie nach einer Operation Unterstützung benötigen.
            </p>
            <ul className="hausbesuche-list">
              <li>Physiotherapie in gewohnter Umgebung</li>
              <li>Für ältere Patienten und bei eingeschränkter Mobilität</li>
              <li>Nach Operationen und bei akuten Beschwerden</li>
              <li>Auf Verordnung Ihres Arztes</li>
            </ul>
            <Button asChild size="lg" className="cta-button">
              <Link to="/hausbesuche">Mehr erfahren</Link>
            </Button>
          </div>
          <div className="hausbesuche-image">
            <img 
              src="https://customer-assets.emergentagent.com/job_physiograu/artifacts/qpc58n3j_IMG_7218.JPG" 
              alt="Behandlungsraum Physiotherapie"
            />
          </div>
        </div>
      </section>

      {/* Personal Training Teaser */}
      <section className="pt-teaser-section">
        <div className="section-container pt-teaser-content">
          <div className="pt-teaser-image">
            <img 
              src="https://customer-assets.emergentagent.com/job_physiograu/artifacts/sc0xj02g_IMG_7220.JPG" 
              alt="Personal Training Bereich"
            />
          </div>
          <div className="pt-teaser-text">
            <h2 className="section-title">Personal Training</h2>
            <p className="pt-teaser-description">
              Individuelles Training für Ihre persönlichen Ziele. Ob Prävention, 
              Leistungssteigerung oder Gewichtsregulation – wir erstellen einen 
              maßgeschneiderten Trainingsplan für Sie.
            </p>
            <p className="pt-notice">
              Selbstzahlerleistung – keine Kassenleistung
            </p>
            <Button asChild size="lg" variant="outline" className="pt-button">
              <Link to="/personal-training">
                Personal Training entdecken
                <ChevronRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="contact-info-section">
        <div className="section-container">
          <h2 className="section-title">Kontakt & Öffnungszeiten</h2>
          <div className="contact-grid">
            <Card className="contact-card">
              <CardContent className="contact-card-content">
                <Phone className="contact-icon" />
                <h3 className="contact-title">Telefon</h3>
                <p className="contact-detail">03644 515313</p>
                <p className="contact-note">Terminvereinbarung telefonisch</p>
              </CardContent>
            </Card>
            <Card className="contact-card">
              <CardContent className="contact-card-content">
                <MapPin className="contact-icon" />
                <h3 className="contact-title">Standort</h3>
                <p className="contact-detail">Rosestraße 4</p>
                <p className="contact-detail">99510 Apolda</p>
              </CardContent>
            </Card>
            <Card className="contact-card">
              <CardContent className="contact-card-content">
                <Clock className="contact-icon" />
                <h3 className="contact-title">Öffnungszeiten</h3>
                <p className="contact-detail">Mo - Fr: 8:00 - 18:00 Uhr</p>
                <p className="contact-note">Termine nach Vereinbarung</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;